import { useEffect, useState } from "react";
import { Button } from "@/components/ui/button";
import { useToast } from "@/context/ToastContext";
import { useConfirm } from "@/context/ConfirmContext";
import { api, unwrapArray } from "@/lib/api";

interface Dup {
  id: string;
  name: string;
  reasons: string[];
  email?: string | null;
  phone?: string | null;
  gstin?: string | null;
  owner?: string | null;
  contacts?: number;
  deals?: number;
  account?: { id: string; name: string } | null;
  same_account?: boolean;
  job_title?: string | null;
}

/**
 * Records that look like this one (same GSTIN, name, email domain or phone for an account; same email, phone or
 * name for a contact), with a button to fold a duplicate into this record. Shows nothing when there are none.
 */
export function Duplicates({ type, id, canMerge, onMerged }: { type: "ACCOUNT" | "CONTACT"; id: string; canMerge: boolean; onMerged: () => void }) {
  const { success, error } = useToast();
  const confirm = useConfirm();
  const [rows, setRows] = useState<Dup[]>([]);
  const [busy, setBusy] = useState<string | null>(null);
  const noun = type === "ACCOUNT" ? "account" : "contact";

  const load = () =>
    api
      .get(`/api/Customer/${type === "ACCOUNT" ? "AccountDuplicates" : "ContactDuplicates"}/${id}`)
      .then((r) => setRows(unwrapArray<Dup>(r)))
      .catch(() => setRows([]));

  useEffect(() => {
    load();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [id, type]);

  if (rows.length === 0) return null;

  const merge = async (d: Dup) => {
    const ok = await confirm({
      title: `Merge "${d.name}" into this ${noun}?`,
      message: `Everything on "${d.name}" (${type === "ACCOUNT" ? "contacts, deals, quotes, proposals, tasks, files and history" : "deals, tasks and history"}) moves to this ${noun}, and "${d.name}" is removed. This cannot be undone.`,
      confirmLabel: "Merge",
      destructive: true,
    });
    if (!ok) return;
    setBusy(d.id);
    try {
      await api.post(`/api/Customer/${type === "ACCOUNT" ? "MergeAccounts" : "MergeContacts"}`, { keep_id: id, merge_id: d.id });
      success("Merged", `"${d.name}" is now part of this ${noun}.`);
      onMerged();
    } catch (e: any) {
      error("Could not merge", e?.message || "Try again");
    } finally {
      setBusy(null);
    }
  };

  return (
    <section className="flex flex-col gap-2 rounded-lg bg-warning-soft px-3.5 py-3">
      <h2 className="text-[11.5px] font-semibold uppercase tracking-wide text-warning">Possible duplicates · {rows.length}</h2>
      {rows.map((d) => (
        <div key={d.id} className="flex flex-col gap-1 rounded-md bg-card/70 px-3 py-2">
          <div className="flex items-start justify-between gap-2">
            <div className="min-w-0">
              <a href={type === "ACCOUNT" ? `/accounts/${d.id}` : `/contacts/${d.id}`} className="text-[13px] font-semibold text-primary hover:underline">
                {d.name}
              </a>
              <div className="text-xs text-muted-foreground">
                {[d.job_title, d.account && !d.same_account ? d.account.name : null, d.gstin, d.email, d.phone, d.owner ? `owned by ${d.owner}` : null].filter(Boolean).join(" · ")}
              </div>
              {(d.contacts !== undefined || d.deals !== undefined) && (
                <div className="text-xs text-muted-foreground">
                  {d.contacts ?? 0} contacts · {d.deals ?? 0} deals
                </div>
              )}
              <div className="mt-0.5 flex flex-wrap gap-1">
                {d.reasons.map((r) => (
                  <span key={r} className="rounded bg-warning/15 px-1.5 text-[10.5px] font-medium text-warning">
                    {r}
                  </span>
                ))}
              </div>
            </div>
            {canMerge && (type === "ACCOUNT" || d.same_account) && (
              <Button size="sm" variant="outline" disabled={busy === d.id} onClick={() => merge(d)}>
                Merge into this
              </Button>
            )}
          </div>
          {canMerge && type === "CONTACT" && !d.same_account && <span className="text-[11px] text-muted-foreground">Different company: merge them from the account first.</span>}
        </div>
      ))}
    </section>
  );
}
